import createClient, { type Client, type Middleware } from 'openapi-fetch';

import { ApiClientError } from './errors';
import type { paths } from './schema';

export type ApiClient = Client<paths>;

export const CSRF_COOKIE = 'sm_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';
export const SYNC_PROTOCOL_HEADER = 'X-Sync-Protocol';
export const API_PREFIX = '/v1';

/** Korelaciona zaglavlja (vidi `api/openapi.yaml` → components.parameters i `docs/observability.md`). */
export const REQUEST_ID_HEADER = 'X-Request-Id';
export const CLIENT_HEADERS = {
  client: 'X-Client',
  appVersion: 'X-App-Version',
  appBuild: 'X-App-Build',
  updateId: 'X-Update-Id',
  updateChannel: 'X-Update-Channel',
  deviceId: 'X-Device-Id',
} as const;

/** Podaci o klijentu koji idu uz svaki zahtjev (log i Sentry na serveru; bez ličnih podataka). */
export interface ClientInfo {
  client: 'web' | 'ios' | 'android';
  appVersion?: string | null;
  /** Web: build id; mobilna: `runtimeVersion`. */
  appBuild?: string | null;
  /** Mobilna: ID aktivnog EAS Update paketa. */
  updateId?: string | null;
  /** Mobilna: EAS Update kanal. */
  updateChannel?: string | null;
  /** Mobilna: stabilan ID instalacije (`lib/device`). */
  deviceId?: string | null;
}

const SAFE_VALUE = /^[A-Za-z0-9._:+-]{1,64}$/;

/** Zaglavlja iz `ClientInfo` (vrijednosti koje nisu bezbjedne se izostavljaju, kao i na serveru). */
export function clientInfoHeaders(info: ClientInfo): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, header] of Object.entries(CLIENT_HEADERS)) {
    const v = info[key as keyof ClientInfo];
    if (typeof v === 'string' && SAFE_VALUE.test(v)) out[header] = v;
  }
  return out;
}

type RandomBytes = (bytes: Uint8Array) => Uint8Array;

/**
 * UUIDv4 za `X-Request-Id`. Koristi `crypto.randomUUID`/`getRandomValues` kad postoje (pregledač, Node);
 * React Native prosljeđuje izvor slučajnih bajtova (expo-crypto).
 */
export function newRequestId(random?: RandomBytes): string {
  const c = (
    globalThis as {
      crypto?: { randomUUID?: () => string; getRandomValues?: (b: Uint8Array) => Uint8Array };
    }
  ).crypto;
  if (!random && typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (random) random(b);
  else if (typeof c?.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Kratka šifra greške za prikaz korisniku („Šifra greške: ab12cd34“): prvih 8 znakova request ID-ja. */
export function shortRequestId(id: string | null | undefined): string | null {
  if (!id) return null;
  const clean = id.replace(/^[a-z]+-/, '').replace(/-/g, '');
  return clean.slice(0, 8) || null;
}

export interface CreateApiClientOptions {
  /** Osnovni URL API-ja bez `/v1`, npr. `https://api.smartmeasure.app` ili `http://localhost:8080`. */
  baseUrl: string;
  /**
   * Mobilna: vraća neproziran session token (iz SecureStore). Ako vrati vrijednost,
   * šalje se `Authorization: Bearer <token>` i CSRF se ne koristi.
   */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /**
   * Web: `include` (podrazumijevano kad nema `getToken`) da bi se slao cookie `__Host-sm_session`.
   */
  credentials?: 'include' | 'omit' | 'same-origin';
  /** Web: čita CSRF token; podrazumijevano iz `document.cookie` (`sm_csrf`). */
  getCsrfToken?: () => string | null | undefined;
  /** Jezik korisnika za `Accept-Language` (npr. `sr-Latn`, `en`). */
  getLanguage?: () => string | null | undefined;
  /** Poziva se na svaki 401 (npr. preusmjeravanje na prijavu ili traženje ponovne prijave na mobilnoj). */
  onUnauthorized?: (err: ApiClientError) => void;
  /** Dodatna zaglavlja za svaki zahtjev. */
  headers?: Record<string, string>;
  /**
   * Podaci o klijentu za korelacione zaglavlja (`X-Client`, `X-App-Version`, `X-App-Build`, `X-Update-Id`,
   * `X-Update-Channel`, `X-Device-Id`). Funkcija se čita pri svakom zahtjevu (npr. ID uređaja stiže kasnije).
   */
  clientInfo?: ClientInfo | (() => ClientInfo);
  /**
   * Izvor slučajnih bajtova za `X-Request-Id` (React Native bez `crypto`). Svaki zahtjev dobija novi UUIDv4,
   * osim ako pozivalac već postavi `X-Request-Id` (ponovni pokušaj istog logičkog zahtjeva).
   */
  randomBytes?: RandomBytes;
  /** Vlastiti fetch (testovi, React Native polyfill). */
  fetch?: typeof globalThis.fetch;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Čita vrijednost cookieja iz `document.cookie` (samo u pregledaču). */
export function readCookie(name: string): string | null {
  const doc = (globalThis as { document?: { cookie?: string } }).document;
  if (!doc?.cookie) return null;
  for (const part of doc.cookie.split(';')) {
    const [k, ...rest] = part.trim().split('=');
    if (k === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

function trimSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/**
 * Tipizirani klijent za Smart Measure API.
 *
 * Web:    `createApiClient({ baseUrl: import.meta.env.VITE_API_URL })`
 * Mobilna: `createApiClient({ baseUrl, getToken: () => SecureStore.getItemAsync('sm_token') })`
 *
 * Pozivi vraćaju `{ data, error, response }` (openapi-fetch). Za stil sa izuzecima koristiti `unwrap()`.
 */
export function createApiClient(opts: CreateApiClientOptions): ApiClient {
  const usesBearer = typeof opts.getToken === 'function';
  const client = createClient<paths>({
    baseUrl: `${trimSlash(opts.baseUrl)}${API_PREFIX}`,
    credentials: opts.credentials ?? (usesBearer ? 'omit' : 'include'),
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
    ...(opts.headers ? { headers: opts.headers } : {}),
  });

  const auth: Middleware = {
    async onRequest({ request }) {
      if (!request.headers.get(REQUEST_ID_HEADER))
        request.headers.set(REQUEST_ID_HEADER, newRequestId(opts.randomBytes));
      if (opts.clientInfo) {
        const info = typeof opts.clientInfo === 'function' ? opts.clientInfo() : opts.clientInfo;
        for (const [k, v] of Object.entries(clientInfoHeaders(info)))
          if (!request.headers.has(k)) request.headers.set(k, v);
      }
      const lang = opts.getLanguage?.();
      if (lang && !request.headers.has('Accept-Language'))
        request.headers.set('Accept-Language', lang);

      if (usesBearer) {
        const token = await opts.getToken?.();
        if (token) request.headers.set('Authorization', `Bearer ${token}`);
      } else if (MUTATING.has(request.method.toUpperCase())) {
        const csrf = (opts.getCsrfToken ?? (() => readCookie(CSRF_COOKIE)))();
        if (csrf) request.headers.set(CSRF_HEADER, csrf);
      }
      return request;
    },
    async onResponse({ response }) {
      if (response.status === 401 && opts.onUnauthorized) {
        const body: unknown = await response
          .clone()
          .json()
          .catch(() => null);
        opts.onUnauthorized(
          ApiClientError.fromBody(401, body, response.headers.get('X-Request-Id') ?? undefined),
        );
      }
      // Bez povratne vrijednosti = odgovor nije izmijenjen. Vraćanje istog objekta puca u React Native-u
      // (expo fetch vraća Response koji nije `instanceof` globalnog Response, openapi-fetch tada baca grešku).
      return undefined;
    },
    // Mrežna greška (nema odgovora): greška nosi request ID koji je klijent poslao, da bi se mogao
    // prikazati korisniku i naći u logu proxyja/servera ako je zahtjev ipak stigao.
    onError({ request, error }) {
      if (error instanceof ApiClientError) return error;
      return ApiClientError.network(error, request.headers.get(REQUEST_ID_HEADER) ?? undefined);
    },
  };
  client.use(auth);
  return client;
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Vraća `data` ili baca `ApiClientError`. Mrežne greške (fetch odbijen) postaju `ApiClientError.network`.
 *
 * ```ts
 * const me = await unwrap(api.GET('/me'));
 * ```
 */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  let res: FetchResult<T>;
  try {
    res = await promise;
  } catch (cause) {
    if (cause instanceof ApiClientError) throw cause;
    throw ApiClientError.network(cause);
  }
  if (res.error !== undefined || !res.response.ok) {
    throw ApiClientError.fromBody(
      res.response.status,
      res.error,
      res.response.headers.get('X-Request-Id') ?? undefined,
    );
  }
  return res.data as T;
}

/** Puni URL za SSE (`EventSource(url, { withCredentials: true })`). */
export function eventsUrl(baseUrl: string): string {
  return `${trimSlash(baseUrl)}${API_PREFIX}/events`;
}

/** Puni URL za PDF dokumenta (za `<iframe src>` u panelu). */
export function documentPdfUrl(baseUrl: string, documentId: string, download = false): string {
  return `${trimSlash(baseUrl)}${API_PREFIX}/documents/${encodeURIComponent(documentId)}/pdf${download ? '?download=true' : ''}`;
}

/** Puni URL za XLSX liste naloga. */
export function productionListXlsxUrl(
  baseUrl: string,
  orderId: string,
  kind: 'cutting' | 'glass' | 'hardware' | 'shutters',
): string {
  return `${trimSlash(baseUrl)}${API_PREFIX}/production/orders/${encodeURIComponent(orderId)}/lists/${kind}/xlsx`;
}
